import { useRef } from "react";

export const JUMP_BACK_KEY = "jump_back";
export const JUMP_FORTH_KEY = "jump_forth";
export const EDITOR_KEY = "editor";
export const SUBJECT_KEY = "subject";
export const SPLIT_SUBJECT_KEY = "split_subject";
export const NOP_KEY = "nop";

export type EmailSubjectKeysType =
  | typeof SUBJECT_KEY
  | typeof SPLIT_SUBJECT_KEY;

export type EmailEditorKeysType =
  | EmailSubjectKeysType
  | typeof JUMP_BACK_KEY
  | typeof JUMP_FORTH_KEY
  | typeof EDITOR_KEY
  | typeof NOP_KEY;

export const setTimeoutAsync = (cb, delay) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(cb());
    }, delay);
  });

export type SharedHistoryInstances = {
  [key in EmailEditorKeysType]?: {
    undo: () => boolean;
    redo: () => void;
    focus: () => void;
    save: (value: string) => void;
  };
};

export type SharedHistory = {
  undo: (callerKey: EmailEditorKeysType) => boolean;
  redo: (callerKey: EmailEditorKeysType) => void;
  save: (callerKey: EmailEditorKeysType, steps?: number) => void;
  instances: SharedHistoryInstances;
  stack: EmailEditorKeysType[];
  cursor: number;
};

export default function useEmailEditorHistory() {
  // This stack stores the order of the change events
  // by stacking up the key of the instance where the
  // event has happened.
  // @ts-ignore
  const sharedHistoryRef = useRef<SharedHistory>({
    instances: {},
    stack: [],
    cursor: 0,
  });

  sharedHistoryRef.current.undo = (callerKey: EmailEditorKeysType) => {
    if (sharedHistoryRef.current.cursor > 0) {
      const key =
        sharedHistoryRef.current.stack[sharedHistoryRef.current.cursor - 1];

      if (key.includes(JUMP_BACK_KEY)) {
        const rawSteps = key.split("/")[1];
        const steps = parseInt(rawSteps);
        sharedHistoryRef.current.cursor--;
        for (let i = 0; i < steps; i++) {
          sharedHistoryRef.current.undo(NOP_KEY);
        }
        return true;
      }

      if (!key.includes(JUMP_FORTH_KEY))
        setTimeoutAsync(
          () => sharedHistoryRef.current.instances[key].focus(),
          0
        ).then(() => sharedHistoryRef.current.instances[key].undo());

      sharedHistoryRef.current.cursor--;
      return true;
    }
    return false;
  };

  sharedHistoryRef.current.redo = (callerKey: EmailEditorKeysType) => {
    if (
      sharedHistoryRef.current.cursor <=
      sharedHistoryRef.current.stack.length - 1
    ) {
      const key =
        sharedHistoryRef.current.stack[sharedHistoryRef.current.cursor];

      if (key.includes(JUMP_FORTH_KEY)) {
        const rawSteps = key.split("/")[1];
        const steps = parseInt(rawSteps);
        sharedHistoryRef.current.cursor++;
        for (let i = 0; i < steps; i++) {
          sharedHistoryRef.current.redo(NOP_KEY);
        }
        return;
      }

      if (!key.includes(JUMP_BACK_KEY))
        setTimeoutAsync(
          () => sharedHistoryRef.current.instances[key].focus(),
          0
        ).then(() => sharedHistoryRef.current.instances[key].redo());

      sharedHistoryRef.current.cursor++;
    }
  };

  sharedHistoryRef.current.save = (
    callerKey: EmailEditorKeysType,
    steps?: number
  ) => {
    if (steps && (callerKey === JUMP_BACK_KEY || callerKey === JUMP_FORTH_KEY))
      callerKey = `${callerKey}/${steps}` as any;

    sharedHistoryRef.current.stack.splice(
      sharedHistoryRef.current.cursor < 0 ? 0 : sharedHistoryRef.current.cursor,
      0,
      callerKey
    );

    sharedHistoryRef.current.cursor++;
    sharedHistoryRef.current.stack.length = sharedHistoryRef.current.cursor;
  };

  return sharedHistoryRef;
}
